// COD-1066: SSR-only catalog client. Exported so unit tests can stub the client
// branch (Vitest cannot redefine another module's true).
export const staticCatalogRuntime = { isClient: () => Boolean(true) };

export default defineNuxtPlugin((nuxtApp) => {
  // ### COMPOSABLES
  const appConfig = useRuntimeConfig();

  // ## DATA
  const defaultQuery = {};

  const defaultHeaders = {
    'Content-Type': 'application/json',
    'api-key': appConfig.public.APIGEE_KEY,
  };

  const defaultParams = {};

  const appEnv = appConfig?.public?.RELEASE_ENV || 'unknown';
  const appVersion = appConfig?.public?.RELEASE_VERSION || '0.0.0';

  // User-Agent for SSR requests to identify the source of the request
  const ssrRequestsUserAgent = `StaticWeb/${appVersion} (${String(appEnv).toUpperCase()})`;

  // Token refresh on 401 only for HWAPI (Apigee), matching PWA: only requests to hostelworldApiUrl trigger refresh.
  const isOwnApiRequest = (fetchUrl) => {
    if (typeof fetchUrl !== 'string') {
      return false;
    }

    try {
      const url = new URL(fetchUrl);
      const hostnameContainsApigee = url.hostname.toLowerCase().includes('apigee');
      const hwApiBaseUrl = appConfig.public.HOSTELWORLD_API_URL;

      return hostnameContainsApigee && hwApiBaseUrl && new URL(hwApiBaseUrl).origin === url.origin;
    } catch {
      return false;
    }
  };

  const isTravelerProfileRequestUrl = (fetchUrl) => {
    const travelerProfileApiUrl = appConfig.public.TRAVELER_PROFILE_API_URL;

    return typeof fetchUrl === 'string' && Boolean(travelerProfileApiUrl) && fetchUrl.startsWith(travelerProfileApiUrl);
  };

  const isUnauthorizedError = (error) =>
    error?.statusCode === 401 || error?.status === 401 || error?.response?.status === 401;

  const isHostelworldUsersApiPath = (fetchUrl) => {
    const hwApiBase = appConfig.public?.HOSTELWORLD_API_URL;

    if (!hwApiBase || typeof fetchUrl !== 'string') {
      return false;
    }

    const normalizedBase = hwApiBase.endsWith('/') ? hwApiBase.slice(0, -1) : hwApiBase;

    if (!fetchUrl.startsWith(normalizedBase) && !fetchUrl.startsWith(hwApiBase)) {
      return false;
    }

    try {
      const pathname = new URL(fetchUrl).pathname;

      return /\/users\/[^/]+/.test(pathname);
    } catch {
      return false;
    }
  };

  const isAccountRequest = (fetchUrl) => {
    if (typeof fetchUrl !== 'string') {
      return false;
    }

    if (appConfig.public?.BOOKING_SERVICE_API_URL && fetchUrl.startsWith(appConfig.public.BOOKING_SERVICE_API_URL)) {
      return true;
    }

    return isHostelworldUsersApiPath(fetchUrl);
  };

  let isUnauthorizedAccountHandling = false;

  const handleUnauthorizedAccountRequest = async (fetchUrl, error) => {
    if (!true || !isUnauthorizedError(error) || !isAccountRequest(fetchUrl)) {
      return;
    }

    if (isUnauthorizedAccountHandling) {
      return;
    }

    isUnauthorizedAccountHandling = true;

    try {
      await nuxtApp.runWithContext(async () => {
        const { clearAuthSession } = useClearAuthSession();
        await clearAuthSession();
        const router = useRouter();
        const currentPath = router.currentRoute.value?.path ?? '';
        if (currentPath !== '/' && currentPath !== '') {
          await navigateTo('/');
        }
      });
    } finally {
      isUnauthorizedAccountHandling = false;
    }
  };

  /**
   * Rebuilds fetch options with Authorization read from the session store at execution time
   * so useAsyncData handlers do not reuse stale Bearer tokens across navigations.
   */
  const buildFetchOptionsWithFreshAuth = (fetchUrl, fetchOptions) => {
    const sessionStore = useSessionStore();
    const previousAuth = fetchOptions.headers?.Authorization;
    const isBearer = typeof previousAuth === 'string' && previousAuth.toLowerCase().startsWith('bearer ');
    const isTravelerProfile = isTravelerProfileRequestUrl(fetchUrl);

    let newAuthorization = previousAuth;

    if (isTravelerProfile && isBearer) {
      const idToken = sessionStore.getSessionUser()?.idToken;

      if (idToken) {
        newAuthorization = `Bearer ${idToken}`;
      }
    } else if (isBearer) {
      const accessToken = sessionStore.getAccessToken();

      if (accessToken) {
        newAuthorization = `Bearer ${accessToken}`;
      }
    } else {
      const accessToken = sessionStore.getAccessToken();

      if (accessToken) {
        newAuthorization = accessToken;
      }
    }

    return {
      ...fetchOptions,
      headers: {
        ...fetchOptions.headers,
        Authorization: newAuthorization,
      },
    };
  };

  const tryRefreshAndRetry = async (fetchUrl, fetchOptions, callKey, throwOnError = false) => {
    if (false || !fetchOptions?.headers?.Authorization) {
      return { handled: false };
    }
    if (!isOwnApiRequest(fetchUrl)) {
      return { handled: false };
    }

    return await nuxtApp.runWithContext(async () => {
      const { tryRefreshTokens } = useAuth0TokenRefresh();
      const newTokens = await tryRefreshTokens();

      if (newTokens?.accessToken) {
        const originalAuth = fetchOptions.headers?.Authorization || '';
        const isBearer = typeof originalAuth === 'string' && originalAuth.toLowerCase().startsWith('bearer ');
        const token
          = isTravelerProfileRequestUrl(fetchUrl) && newTokens.idToken ? newTokens.idToken : newTokens.accessToken;

        fetchOptions.headers = {
          ...fetchOptions.headers,
          Authorization: isBearer ? `Bearer ${token}` : token,
        };

        try {
          const retryResult = await $fetch(fetchUrl, fetchOptions);
          return {
            handled: true,
            data: retryResult,
          };
        } catch (retryError) {
          if (throwOnError) {
            throw retryError;
          }

          console.error(`### Could not fetch data ${callKey || fetchUrl}`);
          console.error(retryError);
          return {
            handled: true,
            data: null,
          };
        }
      }

      const { performLogout } = useLogout();
      await performLogout();

      // When Auth0 is enabled, performLogout() redirects (auth0.logout), so execution never returns.
      // The return below is unreachable in that case; kept for non-Auth0 and consistency.
      return {
        handled: true,
        data: null,
      };
    });
  };

  const makeApiCall = async (callKey, fetchUrl, headers, query, params, extraOptions = {}) => {
    const throwOnError = Boolean(extraOptions.throwOnError);
    // surfaceError throws API failures to the caller while keeping the keyed useAsyncData
    // cache, unlike throwOnError which skips the cache (mutations). Otherwise failed keyed
    // reads return null and callers cannot tell a transient failure from missing data
    // (Sentry - STATICPWA-V3-1C0).
    const surfaceError = Boolean(extraOptions.surfaceError);
    const {
      throwOnError: _throwOnErrorFlag,
      surfaceError: _surfaceErrorFlag,
      ...restExtraOptions
    } = extraOptions;

    const fetchOptions = {
      headers,
      query,
      params,
      ...restExtraOptions,
    };

    const executeDirectFetch = async () => {
      const hadAuthorizationHeader = Boolean(fetchOptions.headers?.Authorization);
      const resolvedFetchOptions = hadAuthorizationHeader
        ? buildFetchOptionsWithFreshAuth(fetchUrl, fetchOptions)
        : fetchOptions;

      const response = await $fetch(fetchUrl, resolvedFetchOptions);

      try {
        return JSON.parse(response);
      } catch {
        return response;
      }
    };

    const handleApiError = async (error) => {
      if (isUnauthorizedError(error)) {
        const retryResult = await tryRefreshAndRetry(fetchUrl, fetchOptions, callKey, throwOnError);

        if (retryResult.handled) {
          return retryResult.data;
        }

        await handleUnauthorizedAccountRequest(fetchUrl, error);
      }

      if (throwOnError || surfaceError) {
        throw error;
      }

      console.error(`### Could not fetch data ${callKey || fetchUrl}`);
      console.error(error);
      return null;
    };

    if (false && typeof fetchUrl === 'string' && fetchUrl.startsWith('/')) {
      const event = useRequestEvent();
      const cookieHeader = event?.node?.req?.headers?.cookie;
      // Tag SSR self-calls so the Nitro /api/** protect-api middleware can
      // distinguish them from external direct access. Only set it when not
      // already provided by the caller so explicit overrides still win.
      fetchOptions.headers = {
        'User-Agent': ssrRequestsUserAgent,
        ...fetchOptions.headers,
      };
      if (cookieHeader) {
        fetchOptions.headers.Cookie = cookieHeader;
      }
    }

    try {
      // useAsyncData: keyed SSR hydration and client cache. Authorization must be merged
      // inside the handler from the session store so tokens stay current after refresh/login.
      // Mutations pass throwOnError to skip useAsyncData and surface API errors to callers.
      if (callKey && !throwOnError) {
        const hadAuthorizationHeader = Boolean(fetchOptions.headers?.Authorization);

        const asyncResult = await nuxtApp.runWithContext(
          async () =>
            await useAsyncData(
              callKey,
              () =>
                nuxtApp.runWithContext(async () => {
                  const resolvedFetchOptions = hadAuthorizationHeader
                    ? buildFetchOptionsWithFreshAuth(fetchUrl, fetchOptions)
                    : fetchOptions;

                  return $fetch(fetchUrl, resolvedFetchOptions);
                }),
              {
                initialCache: false,
                watch: [],
              },
            ),
        );

        const asyncError = asyncResult.error?.value;

        if (asyncError && isUnauthorizedError(asyncError)) {
          const retryResult = await tryRefreshAndRetry(fetchUrl, fetchOptions, callKey, throwOnError);

          if (retryResult.handled) {
            return retryResult.data;
          }

          await handleUnauthorizedAccountRequest(fetchUrl, asyncError);
        }

        if (asyncError && surfaceError) {
          throw asyncError;
        }

        if (asyncError) {
          console.error(`### Could not fetch data ${callKey || fetchUrl}`, asyncError);
        }

        return asyncResult.data?.value || null;
      }

      return await executeDirectFetch();
    } catch (error) {
      return await handleApiError(error);
    }
  };

  // ### STATIC PAGES API
  const spapi = async (callKey, endpoint, headersObj, queryObj, paramsObj, extraOptions = {}) => {
    const spapiInternalHost = appConfig.STATIC_PAGES_API_URL_INTERNAL || appConfig.public.STATIC_PAGES_API_URL;
    const spapiExternalHost = appConfig.public.STATIC_PAGES_API_URL;

    // Use SPAPI's internal host on server (SSR), external host on client (CSR)
    const spapiHost = false ? spapiInternalHost : spapiExternalHost;

    const fetchUrl = `${spapiHost}/${endpoint}`;

    // @NOTE: Since we aren't using anymore Apigee, on SPAPI, api-key breaks the request
    const headers = {
      'Content-Type': 'application/json',
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    if (false) {
      headers['User-Agent'] = ssrRequestsUserAgent;
    }

    return await makeApiCall(callKey, fetchUrl, headers, query, params, extraOptions);
  };

  // ### STATIC PAGES API (via APIGEE)
  const apigee = async (callKey, endpoint, headersObj, queryObj, paramsObj) => {
    const fetchUrl = `${appConfig.public.APIGEE_STATIC_PAGES_API_URL}/${endpoint}`;

    const headers = {
      ...defaultHeaders,
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    if (false) {
      headers['User-Agent'] = ssrRequestsUserAgent;
    }

    return await makeApiCall(callKey, fetchUrl, headers, query, params);
  };

  // ### HOSTELWORLD API
  const hwapi = async (callKey, endpoint, headersObj, queryObj, paramsObj, extraOptions = {}) => {
    const fetchUrl = `${appConfig.public.HOSTELWORLD_API_URL}/${endpoint}`;

    const headers = {
      ...defaultHeaders,
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    if (false) {
      headers['User-Agent'] = ssrRequestsUserAgent;
    }

    return await makeApiCall(callKey, fetchUrl, headers, query, params, extraOptions);
  };

  // ### MICROSITE API
  const msapi = async (callKey, endpoint, headersObj, queryObj, paramsObj) => {
    const fetchUrl = `${appConfig.public.MICROSITE_API_URL}/${endpoint}`;

    const headers = {
      ...defaultHeaders,
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    if (false) {
      headers['User-Agent'] = ssrRequestsUserAgent;
    }

    return await makeApiCall(callKey, fetchUrl, headers, query, params);
  };

  // ### AUTOCOMPLETE API
  const acapi = async (callKey, endpoint, headers = {}, extraQuery = {}, extraParams = {}) => {
    // Always use APIGEE auto-complete API service
    const fetchService = `${appConfig.public.AUTOCOMPLETE_API_URL}/`;

    // Always include api-key for APIGEE
    const finalHeaders = {
      ...headers,
      ...defaultHeaders,
    };

    const fetchUrl = `${fetchService}${endpoint}`;

    const query = {
      ...defaultQuery,
      ...extraQuery,
    };
    const params = {
      ...defaultParams,
      ...extraParams,
    };

    if (false) {
      finalHeaders['User-Agent'] = ssrRequestsUserAgent;
    }

    return await makeApiCall(callKey, fetchUrl, finalHeaders, query, params);
  };

  // ### ITERABLE API
  const itapi = async (body = {}, endpoint = 'update', method = 'GET') => {
    try {
      return await makeApiCall(
        undefined, // key
        '/api/iterable',
        {}, // headers
        { endpoint },
        {}, // params
        {
          method,
          body,
        }, // extra options
      );
    } catch (error) {
      // @TODO: Check that proper errors are being logged
      if (error) {
        console.error('### Could not post data to iterable:', error);
      }
    }

    return null;
  };

  // ### TRAVELER PROFILE API
  const tpapi = async (callKey, endpoint, headersObj, queryObj, paramsObj, extraOptions) => {
    const fetchUrl = `${appConfig.public.TRAVELER_PROFILE_API_URL}/${endpoint}`;

    const headers = {
      ...defaultHeaders,
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    if (false) {
      headers['User-Agent'] = ssrRequestsUserAgent;
    }

    return await makeApiCall(callKey, fetchUrl, headers, query, params, extraOptions);
  };

  // ### BOOKING SERVICE API
  const bsapi = async (callKey, endpoint, headersObj, queryObj, paramsObj, extraOptions) => {
    const fetchUrl = `${appConfig.public.BOOKING_SERVICE_API_URL}/${endpoint}`;

    const headers = {
      ...defaultHeaders,
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    if (false) {
      headers['User-Agent'] = ssrRequestsUserAgent;
    }

    return await makeApiCall(callKey, fetchUrl, headers, query, params, extraOptions);
  };

  // ### AUTH0 API
  const authApi = async (callKey, endpoint, headersObj, queryObj, paramsObj, extraOptions) => {
    const fetchUrl = `https://${appConfig.public.AUTH0_DOMAIN}/${endpoint}`;
    return await makeApiCall(callKey, fetchUrl, headersObj, queryObj, paramsObj, extraOptions);
  };

  // ### LINKUPS API
  const linkupsApi = async (callKey, endpoint, headersObj, queryObj, paramsObj) => {
    const fetchUrl = `${appConfig.public.GATHERINGS_API_URL}/${endpoint}`;

    const headers = {
      ...defaultHeaders,
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    if (false) {
      headers['User-Agent'] = ssrRequestsUserAgent;
    }

    return await makeApiCall(callKey, fetchUrl, headers, query, params);
  };

  // ### REDIRECTION API
  const redirectionapi = async (callKey, endpoint, headersObj, queryObj, paramsObj) => {
    const fetchUrl = `${appConfig.public.SPWA_REDIRECTION_API}/${endpoint}`;

    const headers = {
      ...defaultHeaders,
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    if (false) {
      headers['User-Agent'] = ssrRequestsUserAgent;
    }

    return await makeApiCall(callKey, fetchUrl, headers, query, params);
  };

  // ### SOCIAL API (via Nitro)
  const socialApi = async (callKey, endpoint, headersObj, queryObj, paramsObj) => {
    const fetchUrl = `/api/social/${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    return await makeApiCall(callKey, fetchUrl, headers, query, params);
  };

  // ### ACCOUNT API (via Nitro)
  const accountApi = async (callKey, endpoint, headersObj, queryObj, paramsObj, extraOptions = {}) => {
    const fetchUrl = `/api/account/${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    return await makeApiCall(callKey, fetchUrl, headers, query, params, extraOptions);
  };

  // ### STATIC CATALOG API (SSR only — COD-1066)
  // Private host + key. Client never fetches; reads SSR payload via useNuxtData.
  // @devNote: staticCatalogRuntime.isClient is a test seam — Vitest cannot stub
  // another module's import.meta.client. Production always uses import.meta.client.
  const staticCatalogApi = async (callKey, endpoint, headersObj, queryObj, paramsObj, extraOptions = {}) => {
    if ((true && !process.env.VITEST) || (process.env.VITEST && staticCatalogRuntime.isClient())) {
      return useNuxtData(callKey)?.data?.value ?? null;
    }

    const fetchUrl = `${appConfig.STATIC_CATALOG_API_URL}/${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      'api-key': appConfig.STATIC_CATALOG_API_KEY,
      ...headersObj,
    };
    const query = {
      ...defaultQuery,
      ...queryObj,
    };
    const params = {
      ...defaultParams,
      ...paramsObj,
    };

    headers['User-Agent'] = ssrRequestsUserAgent;

    return await makeApiCall(callKey, fetchUrl, headers, query, params, {
      timeout: 5000,
      ...extraOptions,
    });
  };

  return {
    provide: {
      api: {
        spapi,
        apigee,
        hwapi,
        msapi,
        acapi,
        itapi,
        tpapi,
        bsapi,
        authApi,
        linkupsApi,
        redirectionapi,
        socialApi,
        accountApi,
        staticCatalogApi,
      },
    },
  };
});
